import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Calendar, Edit, XCircle, Eye, Plus, CreditCard, Trash2, X, Copy, Sparkles, FileSpreadsheet } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import AdminEventEditModal from '@/components/AdminEventEditModal';
import { formatEventDate } from '@/lib/formatEventDate';
import { useAuth } from '@/contexts/AuthContext';

const TYPE_COLORS = { teaching: '#56c6df', tournament: '#df5681', casual: '#56dfb4', special: '#b456df' };

// Detect host-error mismatch: event is marked free but the description /
// cost-description / title mentions a dollar amount. Caught Rene's bug
// (Jun 14 2026 — "my event is listed as free and there's a $20 fee").
// Pattern: a $ followed by digits, or "<n> dollars". Cheap regex — false
// positives are fine since the pill is a *suggestion* to review, not a block.
const PRICE_PATTERN = /\$\s?\d+(\.\d{1,2})?|\b\d+\s*dollars?\b/i;
function hasPricingMismatch(ev) {
  if (!ev.is_free) return false;
  const haystack = `${ev.description || ''} ${ev.cost_description || ''} ${ev.title || ''}`;
  return PRICE_PATTERN.test(haystack);
}

export default function MyEventsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const freePosts = user?.event_free_posts_remaining || 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEvent, setEditingEvent] = useState(null);
  const [duplicatingId, setDuplicatingId] = useState(null);
  // PayPal-resume state for draft recovery
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [resumeOrder, setResumeOrder] = useState(null); // { orderId, eventId, fee }
  const [resumingId, setResumingId] = useState(null);

  useEffect(() => {
    api.get('/events/my-events').then(({ data }) => { setEvents(data.events || []); setLoading(false); }).catch(() => setLoading(false));
    api.get('/paypal/config').then(({ data }) => setPaypalConfig(data)).catch(() => {});
  }, []);

  // Coming from the Event Detail "Duplicate" button → auto-open the edit
  // modal on the new draft so the host doesn't have to hunt for it.
  useEffect(() => {
    const newDraftId = searchParams.get('duplicated');
    if (!newDraftId || !events.length) return;
    const draft = events.find(e => e.event_id === newDraftId);
    if (draft) {
      setEditingEvent(draft);
      const next = new URLSearchParams(searchParams);
      next.delete('duplicated');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const handleCancel = async (eventId) => {
    if (!window.confirm('Cancel this event? The posting fee is non-refundable.')) return;
    try {
      await api.post(`/events/${eventId}/cancel`);
      setEvents(prev => prev.map(e => e.event_id === eventId ? { ...e, status: 'canceled' } : e));
      toast.success('Event canceled');
    } catch { toast.error('Failed to cancel'); }
  };

  const handleDuplicate = async (event) => {
    setDuplicatingId(event.event_id);
    try {
      const { data } = await api.post(`/events/${event.event_id}/duplicate`);
      const draft = data.event;
      setEvents(prev => [draft, ...prev]);
      toast.success('Draft created — edit the date and details, then complete payment to publish.');
      // Open the edit modal on the new draft so the host can update the
      // date / details before paying.
      setEditingEvent(draft);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to duplicate event');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleResumePayment = async (event) => {
    setResumingId(event.event_id);
    try {
      const { data } = await api.post(`/events/${event.event_id}/resume-checkout`);
      if (data.published) {
        toast.success(data.already_published ? 'This event is already live!' : 'Event published!');
        setEvents(prev => prev.map(e => e.event_id === event.event_id ? { ...e, payment_status: 'published' } : e));
        return;
      }
      if (data.order_id) {
        if (!paypalConfig) {
          toast.error('Payment options are still loading — please try again in a moment.');
          return;
        }
        setResumeOrder({ orderId: data.order_id, eventId: event.event_id, fee: data.fee });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment');
    } finally {
      setResumingId(null);
    }
  };

  const handleDiscardDraft = async (eventId) => {
    if (!window.confirm('Discard this draft event? This cannot be undone.')) return;
    try {
      await api.delete(`/events/${eventId}`);
      setEvents(prev => prev.filter(e => e.event_id !== eventId));
      toast.success('Draft discarded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to discard draft');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="my-events-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-8 gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>My Events</h1>
          <p className="text-slate-500 mt-1">Events you&apos;ve posted</p>
          {freePosts > 0 && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#56dfb4]/15 px-3 py-1 text-xs font-medium text-[#1f8a6a] border border-[#56dfb4]/40"
              data-testid="my-events-free-posts-pill"
            >
              <Sparkles className="w-3.5 h-3.5" />
              You have {freePosts} free event post{freePosts === 1 ? '' : 's'} — your next post is on us
            </div>
          )}
        </div>
        <Link to="/events/post">
          <Button className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] gap-2"><Plus className="w-4 h-4" /> Post Event</Button>
        </Link>
      </div>
      {(user?.is_instructor || user?.role === 'admin') && (
        <div className="mb-6 flex justify-end">
          <Link to="/events/bulk-import">
            <Button
              data-testid="my-events-bulk-upload-cta"
              className="rounded-full bg-white text-[#b456df] border border-[#b456df] hover:bg-[#b456df]/5 gap-2"
            >
              <FileSpreadsheet className="w-4 h-4" /> Bulk upload from spreadsheet
            </Button>
          </Link>
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No events posted yet</p>
          <Link to="/events/post" className="text-[#df5681] hover:underline text-sm mt-2 block">Post your first event</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map(event => {
            const isDraft = event.payment_status === 'draft' && event.status !== 'canceled';
            return (
              <Card
                key={event.event_id}
                className={`border-slate-100 ${isDraft ? 'border-amber-200 bg-amber-50/40' : ''}`}
                data-testid={`my-event-${event.event_id}`}
              >
                <CardContent className="p-5 flex flex-col gap-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="w-2 h-12 rounded-full flex-shrink-0 hidden sm:block" style={{ backgroundColor: TYPE_COLORS[event.event_type] || '#56dfb4' }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{event.title}</h3>
                        <Badge
                          variant={event.status === 'canceled' ? 'destructive' : event.payment_status === 'published' ? 'default' : 'secondary'}
                          className={`rounded-full text-xs capitalize ${isDraft ? 'bg-amber-100 text-amber-800 border-amber-200' : ''}`}
                        >
                          {event.status === 'canceled' ? 'Canceled' : event.payment_status === 'published' ? 'Live' : event.payment_status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        {event.event_date ? formatEventDate(event.event_date) : 'TBD'} | {event.venue_name}{event.city ? `, ${event.city}` : ''}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">Fee: ${(event.posting_fee || 0).toFixed(2)}</p>
                      {event.summer_partner_applied && (
                        <p
                          className="text-[11px] text-emerald-700 mt-0.5 font-medium inline-flex items-center gap-1"
                          data-testid={`my-event-sfp-applied-${event.event_id}`}
                        >
                          <span aria-hidden>☀️</span> Summer Founding Partner — fee waived
                        </p>
                      )}
                      {hasPricingMismatch(event) && (
                        <button
                          type="button"
                          onClick={() => setEditingEvent(event)}
                          className="block mt-1.5 text-[11px] text-amber-900 bg-amber-100 border border-amber-200 rounded-full px-2.5 py-1 font-medium inline-flex items-center gap-1.5 hover:bg-amber-200 transition-colors"
                          data-testid={`my-event-pricing-mismatch-${event.event_id}`}
                          title="Looks like there's a price in your description but the event is marked free — click to review pricing."
                        >
                          <span aria-hidden>⚠️</span>
                          This event is marked <strong>FREE</strong> but mentions a price — review pricing
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Link to={`/events/${event.event_id}`}>
                        <Button variant="ghost" size="sm" className="rounded-full" title="View" data-testid={`my-event-view-${event.event_id}`}><Eye className="w-4 h-4" /></Button>
                      </Link>
                      {event.status !== 'canceled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingEvent(event)}
                          className="rounded-full text-[#b456df] hover:bg-[#b456df]/10"
                          title="Edit"
                          data-testid={`my-event-edit-${event.event_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDuplicate(event)}
                        disabled={duplicatingId === event.event_id}
                        className="rounded-full text-[#56c6df] hover:bg-[#56c6df]/10"
                        title="Duplicate this event as a new draft"
                        data-testid={`my-event-duplicate-${event.event_id}`}
                      >
                        <Copy className={`w-4 h-4 ${duplicatingId === event.event_id ? 'animate-pulse' : ''}`} />
                      </Button>
                      {event.status !== 'canceled' && event.payment_status === 'published' && (
                        <Button variant="ghost" size="sm" onClick={() => handleCancel(event.event_id)} className="rounded-full text-red-400 hover:text-red-500" title="Cancel" data-testid={`my-event-cancel-${event.event_id}`}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Draft recovery banner */}
                  {isDraft && (
                    <div
                      className="rounded-xl border border-amber-200 bg-white p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                      data-testid={`my-event-draft-banner-${event.event_id}`}
                    >
                      <div className="text-sm text-amber-900">
                        <p className="font-medium">This event isn&apos;t live yet</p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          The ${(event.posting_fee || 0).toFixed(2)} posting fee wasn&apos;t completed. Finish payment to publish, or discard the draft.
                        </p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          onClick={() => handleResumePayment(event)}
                          disabled={resumingId === event.event_id || (event.posting_fee > 0 && !paypalConfig)}
                          className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1.5"
                          data-testid={`my-event-resume-pay-${event.event_id}`}
                        >
                          <CreditCard className="w-3.5 h-3.5" />
                          {resumingId === event.event_id
                            ? 'Starting…'
                            : (event.posting_fee > 0 && !paypalConfig
                                ? 'Loading…'
                                : `Complete Payment ($${(event.posting_fee || 0).toFixed(2)})`)}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDiscardDraft(event.event_id)}
                          className="rounded-full border-red-200 text-red-600 hover:bg-red-50 gap-1.5"
                          data-testid={`my-event-discard-draft-${event.event_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Discard
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {editingEvent && (
        <AdminEventEditModal
          event={editingEvent}
          mode="host"
          onClose={() => setEditingEvent(null)}
          onSaved={updated => {
            setEvents(prev => prev.map(e => e.event_id === updated.event_id ? { ...e, ...updated } : e));
            setEditingEvent(null);
          }}
        />
      )}

      {/* PayPal modal for resuming a draft event's payment */}
      {resumeOrder && paypalConfig && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto" data-testid="my-events-resume-paypal-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Complete Payment</h3>
              <button onClick={() => setResumeOrder(null)} className="p-1 hover:bg-slate-100 rounded-full" aria-label="Close" data-testid="my-events-resume-paypal-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-500 mb-4">Posting fee: <strong>${(resumeOrder.fee || 0).toFixed(2)}</strong></p>
            <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: 'USD', intent: 'capture' }}>
              <PayPalButtons
                style={{ layout: 'vertical', shape: 'rect', label: 'pay' }}
                forceReRender={[resumeOrder.orderId]}
                createOrder={() => resumeOrder.orderId}
                onApprove={async (data) => {
                  try {
                    await api.post(`/events/orders/${data.orderID}/capture`);
                    toast.success('Event published!');
                    const eid = resumeOrder.eventId;
                    setResumeOrder(null);
                    navigate(`/events/${eid}`);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Payment capture failed');
                  }
                }}
                onError={(err) => {
                  console.error('PayPal error:', err);
                  toast.error('PayPal error. Please try again.');
                }}
                onCancel={() => {
                  setResumeOrder(null);
                  toast.info('Payment canceled. Your draft is still here.');
                }}
              />
            </PayPalScriptProvider>
          </div>
        </div>
      )}
    </div>
  );
}
